import type { DelegationScope } from '../models/DelegationDto';

/**
 * Service Agent access scopes (agreed 2026-10-05 — Service Agent Flows/SA_ACCESS_SCOPE_PLAN.md).
 *
 * Each scope includes the one before it, and the scope ALONE decides the four permission flags —
 * the investor no longer ticks them by hand. Mirrors DelegationScopes.java on the backend, which
 * stores exactly these flags whatever the browser sends.
 */
export interface DelegationPermissionFlags {
  canViewProfile: boolean;
  canEditKyc: boolean;
  canUploadDocuments: boolean;
  canSubmitForms: boolean;
}

export const SCOPE_PERMISSIONS: Record<DelegationScope, DelegationPermissionFlags> = {
  VIEW_ONLY:       { canViewProfile: true, canEditKyc: false, canUploadDocuments: false, canSubmitForms: false },
  CKYC_ONLY:       { canViewProfile: true, canEditKyc: true,  canUploadDocuments: false, canSubmitForms: false },
  ONBOARDING_ONLY: { canViewProfile: true, canEditKyc: true,  canUploadDocuments: true,  canSubmitForms: false },
  FULL_ONBOARDING: { canViewProfile: true, canEditKyc: true,  canUploadDocuments: true,  canSubmitForms: true },
};

/** A newly assigned agent starts here (the backend parks pending requests with the same scope). */
export const DEFAULT_DELEGATION_SCOPE: DelegationScope = 'ONBOARDING_ONLY';

/** Unknown scope → View Only flags; the backend denies an unknown scope outright. */
export const permissionsForScope = (scope?: string | null): DelegationPermissionFlags =>
  SCOPE_PERMISSIONS[scope as DelegationScope] ?? SCOPE_PERMISSIONS.VIEW_ONLY;

export const SCOPE_LABELS: Record<DelegationScope, string> = {
  VIEW_ONLY: 'View Only',
  CKYC_ONLY: 'CKYC Only',
  ONBOARDING_ONLY: 'Onboarding Only',
  FULL_ONBOARDING: 'Full Onboarding',
};

/**
 * The backend group an Information tab belongs to. Must match the categories the tab's endpoints
 * pass to accessControl.validateAction in ServiceAgentInvestorController.
 */
export type InfoSection = 'KYC' | 'ONBOARDING';

/**
 * Whether the scope reaches a section. CKYC Only stops at the KYC tabs; Onboarding Only and Full
 * Onboarding cover every Information tab. The edit flag is checked separately.
 */
export const scopeCoversSection = (scope: string | null | undefined, section: InfoSection): boolean => {
  switch (scope) {
    case 'CKYC_ONLY':
      return section === 'KYC';
    case 'ONBOARDING_ONLY':
    case 'FULL_ONBOARDING':
      return true;
    default:
      return false; // VIEW_ONLY or unknown
  }
};

/**
 * The note a read-only Information tab shows a Service Agent, or null when the delegation is not
 * the reason (investor's own view, or a tab the agent may edit but which is locked for another
 * reason, e.g. the post-submission re-edit lock).
 */
export const agentReadOnlyNote = (
  perms: { isProxyMode: boolean; canEditKyc: boolean; scope: string },
  section: InfoSection,
): string | null => {
  if (!perms.isProxyMode) return null;
  const label = SCOPE_LABELS[perms.scope as DelegationScope] ?? perms.scope;
  if (!perms.canEditKyc) {
    return `Your access scope (${label}) lets you view this information but not change it. The investor can change the scope in their Permissions Center.`;
  }
  if (!scopeCoversSection(perms.scope, section)) {
    return `This section is outside your access scope (${label}). The investor can change the scope in their Permissions Center.`;
  }
  return null;
};
